// eslint-disable-next-line react-native/split-platform-components
import { NativeModules, Alert, ToastAndroid } from "react-native";
import { launchCamera as rnLaunchCamera, launchImageLibrary as rnLaunchImageLibrary } from "react-native-image-picker";
import { uploadImage } from "@services/api";
import { requestCamera } from "./Permissions";
import { RESULTS } from "react-native-permissions";

// react-native-image-picker v7 response shape:
//   { didCancel?, errorCode?, errorMessage?, assets?: Asset[] }
//   Asset: { uri, fileName, type, fileSize, width, height, duration, ... }
// Legacy callers here expect a FLAT response with { uri, fileName, type,
// didCancel }. Normalize so neither surface has to change.
const normalize = (r) => {
  if (!r) return { didCancel: true };
  if (r.didCancel) return { didCancel: true };
  if (r.errorCode) return { didCancel: false, error: r.errorMessage };
  const a = r.assets && r.assets[0];
  if (!a) return { didCancel: true };
  return {
    didCancel: false,
    uri: a.uri,
    fileName: a.fileName,
    type: a.type,
    fileSize: a.fileSize,
    width: a.width,
    height: a.height,
    duration: a.duration,
  };
};

const buildFormData = (response) => {
  const data = new FormData();
  data.append("image", {
    name: response.fileName,
    type: response.type,
    uri: response.uri,
  });
  return data;
};

// react-native-image-picker merges as { ...DEFAULT_OPTIONS, ...options }, so an
// explicit `undefined` OVERWRITES the library default and is then dropped
// crossing the bridge. Options.java's constructor reads getInt("durationLimit")
// with no hasKey guard, so a missing key is a fatal NoSuchKeyException — omit
// keys we don't want rather than passing undefined.
const compact = (options) =>
  Object.fromEntries(
    Object.entries(options).filter(([, value]) => value !== undefined),
  );

const openCamera = async ({ mediaType, cameraType, durationLimit }) => {
  const isVideo = mediaType === "video";
  const r = await rnLaunchCamera(
    compact({
      mediaType,
      quality: 0.4,
      maxHeight: 2048,
      cameraType,
      saveToPhotos: false,
      videoQuality: isVideo ? "medium" : undefined,
      durationLimit: isVideo ? durationLimit : undefined,
      selectionLimit: 1,
    }),
  );
  return normalize(r);
};

const openGallery = async ({ mediaType }) => {
  const r = await rnLaunchImageLibrary(
    compact({
      mediaType,
      quality: 0.4,
      maxHeight: 2048,
      selectionLimit: 1,
    }),
  );
  return normalize(r);
};

const promptPermissionBlocked = () => {
  const { AppSettingsIntentModule } = NativeModules;
  Alert.alert(
    "Permissions denied",
    "Please grant us Camera access to continue",
    [
      {
        text: "Go to settings",
        onPress: () => AppSettingsIntentModule?.openAppSettings?.(),
      },
      { text: "Cancel", onPress: () => {} },
    ],
    { cancelable: true },
  );
};

/**
 * Shared image / video picker.
 *
 * Back-compat default: source="both" shows the camera/gallery Alert and
 * uploads the picked asset via /media/upload-image (uploadImage). All
 * existing callers (KYCSetup, SupportTree, …) hit this path.
 *
 * Props:
 *   source?: "both" | "camera" | "gallery"        default "both"
 *   mediaType?: "photo" | "video"                 default "photo"
 *   cameraType?: "front" | "back"                 default "back"
 *   durationLimit?: number (seconds, video only)  default none
 *   isCancellable?: boolean                       default true
 *   uploadFn?: (formData) => Promise<{ data }>    default uploadImage
 *   skipUpload?: boolean                          when true, hands the
 *       normalized response to onSuccessCallback without uploading
 *   onSelectCallback?: (response) => void
 *   onSuccessCallback?: (result) => void
 *   onDismiss?: () => void
 */
export default function imagePickerHelper(props) {
  const {
    onSelectCallback = () => {},
    onSuccessCallback = () => {},
    onDismiss = () => {},
    isCancellable = true,
    source = "both",
    mediaType = "photo",
    cameraType = "back",
    durationLimit,
    uploadFn = uploadImage,
    skipUpload = false,
  } = props;

  const handlePicked = async (response) => {
    onSelectCallback(response);
    try {
      if (!response || response.didCancel) return;
      if (!response.uri) return;

      if (skipUpload) {
        onSuccessCallback(response);
        return;
      }

      const { data: uploadData } = await uploadFn(buildFormData(response));
      onSuccessCallback(uploadData);
    } catch (e) {
      ToastAndroid.showWithGravity(
        "Error with uploading image",
        ToastAndroid.SHORT,
        ToastAndroid.CENTER,
      );
    }
  };

  const launchCameraWithPermission = async () => {
    const { granted, statuses } = await requestCamera();
    if (granted) {
      const r = await openCamera({ mediaType, cameraType, durationLimit });
      return handlePicked(r);
    }
    const isBlocked = Object.values(statuses || {}).some(
      (s) => s === RESULTS.BLOCKED,
    );
    if (isBlocked) promptPermissionBlocked();
    return undefined;
  };

  if (source === "camera") {
    launchCameraWithPermission();
    return;
  }

  if (source === "gallery") {
    openGallery({ mediaType }).then(handlePicked);
    return;
  }

  // source === "both" — original Alert UX, preserved for legacy callers.
  Alert.alert(
    "Upload Photo",
    "आप कहाँ से फ़ोटो चुनना चाहते हैं?",
    [
      { text: "कैमरे से", onPress: launchCameraWithPermission },
      {
        text: "गैलरी से",
        onPress: () => openGallery({ mediaType }).then(handlePicked),
      },
    ],
    { cancelable: isCancellable, onDismiss },
  );
}
